import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import mediaManagerRepository from 'modules/manager/domain/repositories/media-manager-repository';
import { TEMPLATE_NAME_CONFLICT_MESSAGE, isTemplateNameConflict } from 'modules/manager/domain/template-name-conflict';
import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

export interface DuplicateTemplateOptions {
  /** Name for the copy. Blank/omitted falls back to `<source> (копия)`. */
  name?: string;
}

export interface UseDuplicateTemplateResult {
  /** True when there is an active tournament to copy into. */
  canDuplicate: boolean;
  /** True while a copy is being created — callers must block their duplicate control. */
  isDuplicating: boolean;
  /** Message for the last failed attempt (e.g. the name is taken), else null. */
  error: string | null;
  /** Clone a template (preset) into the active tournament as a private copy. */
  duplicate: (templateId: number, options?: DuplicateTemplateOptions) => Promise<MediaManagerTemplate | null>;
  /** Clear `error` — call when the form is reopened. */
  resetError: () => void;
}

/**
 * Copies a template (typically a global preset) into the current tournament so
 * the user can edit their own copy. Requires an active tournament.
 */
export function useDuplicateTemplate(): UseDuplicateTemplateResult {
  const tournamentId = useSelector(authStoreUserTournamentId);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A ref rather than the state flag: two clicks in the same tick both read the
  // stale `false` from state before React re-renders the disabled control, so
  // only a synchronous guard actually prevents a second copy.
  const inFlight = useRef(false);

  const duplicate = useCallback(
    async (templateId: number, options?: DuplicateTemplateOptions): Promise<MediaManagerTemplate | null> => {
      if (!tournamentId) {
        console.error('Cannot duplicate a template without an active tournament');
        return null;
      }
      if (inFlight.current) return null;

      inFlight.current = true;
      setIsDuplicating(true);
      setError(null);
      try {
        const name = options?.name?.trim();
        return await mediaManagerRepository.copyTemplate({ templateId, tournamentId, name: name || undefined });
      } catch (err) {
        console.error('Failed to duplicate media manager template:', err);
        // The DB owns name uniqueness, so a taken name arrives here rather than
        // from a client-side check — it must read as a form error, not a dead button.
        setError(isTemplateNameConflict(err) ? TEMPLATE_NAME_CONFLICT_MESSAGE : 'Не удалось создать копию, попробуйте ещё раз');
        return null;
      } finally {
        inFlight.current = false;
        setIsDuplicating(false);
      }
    },
    [tournamentId],
  );

  const resetError = useCallback(() => setError(null), []);

  return { canDuplicate: !!tournamentId, isDuplicating, error, duplicate, resetError };
}
